import { MutationObserver, type MutationOptions, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { graphql, graphqlWrite } from './api'
import { keys, markWrittenBehind, type IssueDetail } from './query'
import type { WorkIssue } from '@/types/github'

// The one optimistic-write lifecycle every issue edit runs through:
// cancel in-flight universe fetches → patch the universe and any open detail →
// send → on error roll back ONLY the patched fields (a whole-array snapshot
// would clobber concurrent edits) and toast Retry → on success toast Undo →
// mark stale the caches the write dirties. Plain functions over a QueryClient;
// the hooks in mutations.ts are thin adapters.

export type Receipt = Awaited<ReturnType<typeof graphqlWrite>>
export type Send = (req: Parameters<typeof graphql>[0]) => Promise<Receipt | undefined>
export type WriteContext = { client: QueryClient; send: Send }
export type WriteVars = { issue: WorkIssue; silent?: boolean }

/** What one kind of write is: its document, its patch, its inverse and its toasts. */
export type IssueWriteKind<V extends WriteVars> = {
  /** Send the document(s). Missing lookups throw here, after the optimistic patch. */
  send: (vars: V, ctx: WriteContext) => Promise<void>
  patch: (vars: V, ctx: WriteContext) => Partial<WorkIssue>
  /** Vars that take this write back off `current` — the issue as it is when Undo is clicked. */
  undo?: (current: WorkIssue, before: Partial<WorkIssue>, vars: V) => V
  /** Omitted = no success toast. */
  success?: (vars: V) => string
  failure: (vars: V) => string
  /** false = the failure toast offers no Retry (the caller keeps the draft to resend). */
  retry?: false
  /** Caches dirtied beyond the universe and the issue's own detail. */
  dirties?: QueryKey[]
  /** Moves the issue out of the open-only universe (close), into it (reopen), or out of every cache (delete). */
  moves?: (vars: V) => 'out' | 'in' | 'gone' | undefined
  /** Detail-only state that isn't a WorkIssue field (an optimistic comment), and its reversal. */
  patchDetail?: (detail: IssueDetail, vars: V) => IssueDetail
  unpatchDetail?: (detail: IssueDetail, vars: V) => IssueDetail
}

/** What onMutate wrote, and what it replaced — the rollback's whole scope. */
type Applied = {
  patch: Partial<WorkIssue>
  before: Partial<WorkIssue>
  bodyHTML: string | null | undefined
  wasOpen: boolean
  /** Where a delete took the issue out of the closed list, to put it back on failure. */
  closedAt: number
}

/** How a run was started: by the caller, or by an Undo click (no success toast, still refetches). */
type Mode = 'normal' | 'undo'

/**
 * Patch BOTH caches that carry the issue: the universe and any open detail
 * (the Sheet reads detail first — patching only the universe made it snap back).
 */
export function patchIssueEverywhere(client: QueryClient, issueId: string, patch: Partial<WorkIssue>): void {
  client.setQueryData<WorkIssue[]>(keys.issues, (issues) =>
    issues?.map((i) => (i.id === issueId ? { ...i, ...patch } : i)),
  )
  client.setQueriesData<IssueDetail>({ queryKey: keys.issueDetails }, (detail) =>
    detail && detail.issue.id === issueId
      ? {
          ...detail,
          issue: { ...detail.issue, ...patch },
          // A body edit invalidates GitHub's rendered HTML — null it so the
          // Sheet falls back to client-side markdown until the refetch.
          ...(patch.body !== undefined && { body: patch.body, bodyHTML: null }),
        }
      : detail,
  )
}

/** Latest copy of an issue from whichever cache has it (closed issues live only in detail). */
export function currentIssue(client: QueryClient, issue: WorkIssue): WorkIssue {
  const fromUniverse = client.getQueryData<WorkIssue[]>(keys.issues)?.find((i) => i.id === issue.id)
  if (fromUniverse) return fromUniverse
  return client.getQueryData<IssueDetail>(keys.issueDetail(issue.number))?.issue ?? issue
}

function ownedValue(client: QueryClient, issue: WorkIssue, key: string): unknown {
  const detail = client.getQueryData<IssueDetail>(keys.issueDetail(issue.number))
  return key === 'body' && detail ? detail.body : currentIssue(client, issue)[key as keyof WorkIssue]
}

/** Put one issue back into the open-only universe (created-asc ordered). */
function insertIssue(client: QueryClient, issue: WorkIssue): void {
  client.setQueryData<WorkIssue[]>(keys.issues, (issues) =>
    issues && !issues.some((i) => i.id === issue.id)
      ? [...issues, issue].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      : issues,
  )
}

function dropIssue(client: QueryClient, queryKey: QueryKey, issueId: string): void {
  client.setQueryData<WorkIssue[]>(queryKey, (issues) => issues?.filter((i) => i.id !== issueId))
}

function patchDetailOf(client: QueryClient, number: number, fn: (detail: IssueDetail) => IssueDetail): void {
  client.setQueryData<IssueDetail>(keys.issueDetail(number), (detail) => detail && fn(detail))
}

/**
 * The one refetch a bulk caller fires after its run of silent writes: every
 * cache that kind of write dirties, each once.
 */
export function refetchAfterBulk<V extends WriteVars>(
  client: QueryClient,
  kind: IssueWriteKind<V>,
  receipts: ReadonlyArray<Receipt | undefined>,
): void {
  const universe = receipts.some((receipt) => receipt?.universeBehind) ? [] : [keys.issues]
  for (const queryKey of [...universe, keys.issueDetails, ...(kind.dirties ?? [])]) {
    void client.invalidateQueries({ queryKey })
  }
}

/**
 * Mark stale once no other mutation is in flight — refetching mid-mutation
 * would overwrite someone else's optimistic patch. isMutating counts the
 * calling mutation too while it settles, hence <= 1.
 */
function invalidateWhenIdle(client: QueryClient, dirty: QueryKey[]): void {
  if (client.isMutating() > 1) return
  for (const queryKey of dirty) void client.invalidateQueries({ queryKey })
}

/** The lifecycle as mutation options — for `useMutation`, or a detached run. */
export function issueWriteOptions<V extends WriteVars>(
  ctx: WriteContext,
  kind: IssueWriteKind<V>,
  mode: Mode = 'normal',
): MutationOptions<Receipt, Error, V, Applied> {
  const { client } = ctx
  const rerun = (vars: V, m: Mode) => void runIssueWrite(ctx, kind, vars, m).catch(() => {})
  return {
    mutationFn: async (vars) => {
      let universeBehind = false
      await kind.send(vars, {
        ...ctx,
        send: async (req) => {
          const receipt = await ctx.send({ ...req, touchedIds: [vars.issue.id] })
          if (receipt?.universeBehind) {
            universeBehind = true
            markWrittenBehind(client, vars.issue.id)
          }
          return receipt
        },
      })
      return { universeBehind }
    },
    onMutate: async (vars) => {
      await Promise.all([
        client.cancelQueries({ queryKey: keys.issues }),
        client.cancelQueries({ queryKey: keys.issueDetail(vars.issue.number) }),
      ])
      const patch = kind.patch(vars, ctx)
      const current = currentIssue(client, vars.issue)
      const before = Object.fromEntries(
        Object.keys(patch).map((k) => [k, ownedValue(client, vars.issue, k)]),
      ) as Partial<WorkIssue>
      const bodyHTML = client.getQueryData<IssueDetail>(keys.issueDetail(vars.issue.number))?.bodyHTML
      const wasOpen = client.getQueryData<WorkIssue[]>(keys.issues)?.some((i) => i.id === vars.issue.id) ?? false
      const moves = kind.moves?.(vars)
      if (moves === 'in') insertIssue(client, { ...current, ...patch })
      patchIssueEverywhere(client, vars.issue.id, patch)
      const { patchDetail } = kind
      if (patchDetail) patchDetailOf(client, vars.issue.number, (d) => patchDetail(d, vars))
      if (moves === 'out' || moves === 'gone') dropIssue(client, keys.issues, vars.issue.id)
      const closedAt = client.getQueryData<WorkIssue[]>(keys.closedIssues)?.findIndex((i) => i.id === vars.issue.id) ?? -1
      if (moves === 'gone') {
        dropIssue(client, keys.closedIssues, vars.issue.id)
        client.removeQueries({ queryKey: keys.issueDetail(vars.issue.number) })
      }
      return { patch, before, bodyHTML, wasOpen, closedAt }
    },
    onError: (err, vars, applied) => {
      if (applied) {
        const moves = kind.moves?.(vars)
        // Membership first, so the field restore below reaches the reinserted copy.
        if (moves && moves !== 'in' && applied.wasOpen) insertIssue(client, currentIssue(client, vars.issue))
        if (moves === 'in' && !applied.wasOpen) dropIssue(client, keys.issues, vars.issue.id)
        if (moves === 'gone' && applied.closedAt >= 0) {
          client.setQueryData<WorkIssue[]>(keys.closedIssues, (issues) => issues?.toSpliced(applied.closedAt, 0, vars.issue))
        }
        const { unpatchDetail } = kind
        if (unpatchDetail) patchDetailOf(client, vars.issue.number, (d) => unpatchDetail(d, vars))
        // Restore only fields still holding our value — a later write owns the rest.
        // By value: structural sharing copies arrays, so references don't survive.
        const same = (k: string) =>
          JSON.stringify(ownedValue(client, vars.issue, k)) === JSON.stringify(applied.patch[k as keyof WorkIssue])
        const ours = Object.fromEntries(Object.entries(applied.before).filter(([k]) => same(k))) as Partial<WorkIssue>
        patchIssueEverywhere(client, vars.issue.id, ours)
        if (ours.body !== undefined && applied.bodyHTML !== undefined) {
          patchDetailOf(client, vars.issue.number, (d) => ({ ...d, bodyHTML: applied.bodyHTML ?? null }))
        }
      }
      if (vars.silent) return
      toast.error(kind.failure(vars), {
        description: err.message,
        action: kind.retry === false ? undefined : { label: 'Retry', onClick: () => rerun(vars, mode) },
      })
    },
    onSuccess: (_data, vars, applied) => {
      const { undo, success } = kind
      if (vars.silent || mode === 'undo' || !success) return
      toast.success(success(vars), {
        action:
          undo && applied
            ? { label: 'Undo', onClick: () => rerun(undo(currentIssue(client, vars.issue), applied.before, vars), 'undo') }
            : undefined,
      })
    },
    // Silent (bulk) runs skip the refetch; the bulk caller fires one after the batch.
    onSettled: (receipt, _e, vars) => {
      if (vars.silent) return
      const universe = receipt?.universeBehind ? [] : [keys.issues]
      invalidateWhenIdle(client, [...universe, keys.issueDetail(vars.issue.number), ...(kind.dirties ?? [])])
    },
  }
}

/** Run one write outside React (Retry, Undo, tests). Rejects with the write's error. */
export function runIssueWrite<V extends WriteVars>(
  ctx: WriteContext,
  kind: IssueWriteKind<V>,
  vars: V,
  mode: Mode = 'normal',
): Promise<Receipt> {
  return new MutationObserver(ctx.client, issueWriteOptions(ctx, kind, mode)).mutate(vars)
}
